import { useState, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { 
  Code2, 
  Play, 
  Copy, 
  Check,
  Eye,
  BookOpen,
  Sparkles,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Save,
  Server,
  Search,
  FileCode,
  Trash2,
  Tag,
  ExternalLink,
  Filter,
  X,
  Folder,
  Hash,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../components/Toast';
import { 
  useCMSAdapters, 
  CMS_DISPLAY_CONFIG,
  UNIVERSAL_FILTERS
} from '../hooks/useCMSAdapters';

interface Grammar {
  version: string;
  parameters: Record<string, {
    required: boolean;
    type: string;
    default?: string | number;
    values?: string[];
  }>;
  placeholders: string[];
  conditionals: string[];
  operators: string[];
}

interface Snippet {
  snippet_id: number;
  snippet_name: string;
  snippet_code: string;
  description: string;
  category: string;
  tags: string[];
}

interface Instance {
  instance_id: string;
  instance_name: string;
  name?: string; // alias
  cms_type: string;
  domain: string;
  status: string;
}

export default function DSLBuilder() {
  const { token } = useAuth();
  const toast = useToast();
  const [query, setQuery] = useState('{ikb_query type=post limit=5 format=card layout=grid-3}');
  const [copied, setCopied] = useState(false);
  const [showGrammar, setShowGrammar] = useState(true);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'builder' | 'snippets' | 'reference'>('builder');
  const [demoMode, setDemoMode] = useState(true);
  const [showAst, setShowAst] = useState(false);
  const [lastAst, setLastAst] = useState<object | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [snippetSearch, setSnippetSearch] = useState('');
  const [snippetCategory, setSnippetCategory] = useState<string>('all');
  const [referenceSearch, setReferenceSearch] = useState('');
  const [referenceCategory, setReferenceCategory] = useState<string>('all');
  
  // Fetch CMS adapters data (filters, content types, etc.) - dynamically updated
  const { data: adaptersData, isLoading: adaptersLoading, refetch: refetchAdapters } = useCMSAdapters();

  // CMS-specific terminology and examples
  const cmsTerminology: Record<string, { 
    contentType: string; 
    contentTypePlural: string;
    excerpt: string;
    author: string;
    date: string;
    category: string;
    tag: string;
  }> = {
    wordpress: { contentType: 'post', contentTypePlural: 'posts', excerpt: 'excerpt', author: 'author', date: 'post_date', category: 'category', tag: 'tag' },
    joomla: { contentType: 'article', contentTypePlural: 'articles', excerpt: 'introtext', author: 'created_by', date: 'created', category: 'catid', tag: 'tags' },
    drupal: { contentType: 'node', contentTypePlural: 'nodes', excerpt: 'body', author: 'uid', date: 'created', category: 'taxonomy', tag: 'tags' },
    generic: { contentType: 'post', contentTypePlural: 'posts', excerpt: 'excerpt', author: 'author', date: 'date', category: 'category', tag: 'tag' },
  };

  // DiSyL Reference Data
  const disylReference = {
    components: [
      { name: 'ikb_query', description: 'Query content from CMS', syntax: '{ikb_query type="post" limit=10}', params: ['type', 'limit', 'offset', 'format', 'layout', 'orderby', 'order', 'status'] },
      { name: 'ikb_section', description: 'Layout section container', syntax: '{ikb_section type="hero" bg="gradient"}...{/ikb_section}', params: ['type', 'bg', 'padding', 'margin'] },
      { name: 'ikb_container', description: 'Content container', syntax: '{ikb_container size="large"}...{/ikb_container}', params: ['size', 'class'] },
      { name: 'ikb_text', description: 'Text element with styling', syntax: '{ikb_text size="xl" weight="bold"}...{/ikb_text}', params: ['size', 'weight', 'color', 'align'] },
      { name: 'ikb_image', description: 'Image element', syntax: '{ikb_image src="{post.thumbnail}" alt="{post.title}" /}', params: ['src', 'alt', 'width', 'height', 'class'] },
      { name: 'ikb_card', description: 'Card component', syntax: '{ikb_card}...{/ikb_card}', params: ['class', 'shadow', 'rounded'] },
      { name: 'ikb_grid', description: 'Grid layout', syntax: '{ikb_grid cols=3 gap="md"}...{/ikb_grid}', params: ['cols', 'gap', 'class'] },
      { name: 'ikb_link', description: 'Link element', syntax: '{ikb_link href="{post.url}"}...{/ikb_link}', params: ['href', 'target', 'class'] },
    ],
    // Filters are populated dynamically from adapters - see dynamicFilters below
    filters: [] as Array<{ name: string; description: string; syntax: string; category: string; cms?: string }>,
    controlStructures: [
      { name: 'if', description: 'Conditional rendering', syntax: '{if condition="{expr}"}...{else}...{/if}', example: '{if condition="{user.role == \'admin\'}"}Admin{else}User{/if}' },
      { name: 'elseif', description: 'Additional condition', syntax: '{elseif condition="{expr}"}', example: '{if condition="{x == 1}"}One{elseif condition="{x == 2}"}Two{else}Other{/if}' },
      { name: 'for', description: 'Loop over items', syntax: '{for items="{collection}" as="item" key="index"}...{empty}...{/for}', example: '{for items="{posts}" as="post"}{post.title}{/for}' },
      { name: 'switch', description: 'Switch statement', syntax: '{switch value="{expr}"}{case match="x"}...{default}...{/switch}', example: '{switch value="{status}"}{case match="active"}✓{default}?{/switch}' },
      { name: 'include', description: 'Include template file', syntax: '{include file="path/to/template.disyl" /}', example: '{include file="components/header.disyl" /}' },
    ],
    operators: [
      { name: '|', description: 'Pipe operator for filters', syntax: '{value | filter1 | filter2}', example: '{title | strip_tags | upper}' },
      { name: '?.', description: 'Safe navigation (null-safe)', syntax: '{object?.property?.nested}', example: '{user?.profile?.avatar}' },
      { name: '==', description: 'Equality comparison', syntax: '{a == b}', example: '{if condition="{status == \'active\'}"}' },
      { name: '!=', description: 'Inequality comparison', syntax: '{a != b}', example: '{if condition="{role != \'guest\'}"}' },
      { name: '&&', description: 'Logical AND', syntax: '{a && b}', example: '{if condition="{logged_in && is_admin}"}' },
      { name: '||', description: 'Logical OR', syntax: '{a || b}', example: '{if condition="{is_admin || is_editor}"}' },
      { name: '[]', description: 'Array access', syntax: '{array[index]}', example: '{items[0].title}' },
    ],
    types: [
      { name: 'string', description: 'Text value' },
      { name: 'number', description: 'Numeric value (integer or float)' },
      { name: 'boolean', description: 'true/false' },
      { name: 'array', description: 'Indexed collection' },
      { name: 'object', description: 'Key-value collection' },
      { name: 'url', description: 'Valid URL string' },
      { name: 'date', description: 'ISO 8601 date' },
      { name: 'html', description: 'HTML content' },
      { name: 'json', description: 'JSON string' },
    ],
  };

  // Generate dynamic filters from adapters API + universal filters
  const dynamicFilters = useMemo(() => {
    const filters: Array<{ name: string; description: string; syntax: string; category: string; cms?: string }> = [];
    
    // Add universal filters first
    Object.entries(UNIVERSAL_FILTERS).forEach(([name, filter]) => {
      let category = 'universal';
      if (['esc_html', 'esc_url', 'esc_attr', 'strip_tags'].includes(name)) category = 'security';
      else if (['upper', 'lower', 'capitalize', 'truncate', 'trim', 'nl2br', 'slug'].includes(name)) category = 'text';
      else if (['date', 'number_format', 'json'].includes(name)) category = 'format';
      else if (['default', 'raw', 'length', 'first', 'last', 'join', 'split', 'sort', 'reverse', 'keys', 'values'].includes(name)) category = 'logic';
      
      filters.push({
        name,
        description: filter.description,
        syntax: filter.example || `{value | ${name}}`,
        category,
      });
    });
    
    // Add CMS-specific filters from adapters
    if (adaptersData?.adapters) {
      Object.entries(adaptersData.adapters).forEach(([cmsId, adapter]) => {
        if (adapter.filters) {
          Object.entries(adapter.filters).forEach(([name, filter]) => {
            // Skip if already added as universal
            if (!filters.find(f => f.name === name)) {
              filters.push({
                name,
                description: filter.description,
                syntax: filter.example || `{value | ${name}}`,
                category: cmsId,
                cms: cmsId,
              });
            }
          });
        }
      });
    }
    
    return filters;
  }, [adaptersData]);

  // Dynamic CMS-aware snippets generator (will be called after currentTerms is defined)
  const getBuiltInSnippets = (t: typeof cmsTerminology.generic, cms: string): Snippet[] => {
    const cmsLabel = cms === 'wordpress' ? 'WordPress' : cms === 'joomla' ? 'Joomla' : cms === 'drupal' ? 'Drupal' : 'CMS';
    
    // Base snippets that work for all CMS
    const baseSnippets: Snippet[] = [
      // Content Queries - Dynamic based on CMS
      { snippet_id: -1, snippet_name: `${cmsLabel} ${t.contentTypePlural.charAt(0).toUpperCase() + t.contentTypePlural.slice(1)} Grid`, snippet_code: `{ikb_query type=${t.contentType} limit=6 format=card layout=grid-3}`, description: `Display ${t.contentTypePlural} in a 3-column grid`, category: 'content', tags: [t.contentTypePlural, 'grid', cms] },
      { snippet_id: -2, snippet_name: `Latest ${t.contentTypePlural.charAt(0).toUpperCase() + t.contentTypePlural.slice(1)}`, snippet_code: `{ikb_query type=${t.contentType} limit=5 format=list orderby=date order=desc}`, description: `List of latest ${t.contentTypePlural}`, category: 'content', tags: [t.contentTypePlural, 'list', 'latest'] },
      { snippet_id: -3, snippet_name: `Featured ${t.contentType.charAt(0).toUpperCase() + t.contentType.slice(1)}`, snippet_code: `{ikb_query type=${t.contentType} limit=1 format=hero}`, description: `Single featured ${t.contentType} as hero`, category: 'content', tags: ['hero', 'featured', t.contentType] },
      { snippet_id: -5, snippet_name: 'Category Navigation', snippet_code: '{ikb_query type=category format=list}', description: 'List all categories', category: 'content', tags: ['categories', 'navigation'] },
      
      // Expressions - Dynamic field names
      { snippet_id: -10, snippet_name: 'Safe Title Output', snippet_code: `{${t.contentType}.title | esc_html}`, description: `Safely output escaped ${t.contentType} title`, category: 'expressions', tags: ['security', 'escape', t.contentType] },
      { snippet_id: -11, snippet_name: 'Truncated Excerpt', snippet_code: `{${t.contentType}.${t.excerpt} | strip_tags | truncate:150 | esc_html}`, description: `Clean and truncate ${t.contentType} excerpt`, category: 'expressions', tags: ['text', 'truncate', t.excerpt] },
      { snippet_id: -12, snippet_name: 'Formatted Date', snippet_code: `{${t.contentType}.${t.date} | date:format="F j, Y"}`, description: 'Human-readable date format', category: 'expressions', tags: ['date', 'format'] },
      { snippet_id: -13, snippet_name: 'Price Format', snippet_code: '{product.price | number_format:2,dec_point=".",thousands_sep=","}', description: 'Format price with decimals', category: 'expressions', tags: ['number', 'price', 'format'] },
      { snippet_id: -14, snippet_name: 'Safe Navigation', snippet_code: '{user?.profile?.avatar | default:"placeholder.png"}', description: 'Null-safe property access with fallback', category: 'expressions', tags: ['safe', 'navigation', 'default'] },
      
      // Control Structures - Dynamic
      { snippet_id: -20, snippet_name: 'Admin Check', snippet_code: '{if condition="{user.role == \'admin\'}"}\n  <div class="admin-panel">Admin Content</div>\n{else}\n  <div class="user-content">User Content</div>\n{/if}', description: 'Show different content for admins', category: 'control', tags: ['if', 'admin', 'roles'] },
      { snippet_id: -21, snippet_name: `${t.contentType.charAt(0).toUpperCase() + t.contentType.slice(1)} Loop`, snippet_code: `{for items="{${t.contentTypePlural}}" as="${t.contentType}" key="index"}\n  <article>\n    <h2>{${t.contentType}.title | esc_html}</h2>\n    <p>{${t.contentType}.${t.excerpt} | truncate:100}</p>\n  </article>\n{empty}\n  <p>No ${t.contentTypePlural} found.</p>\n{/for}`, description: `Loop through ${t.contentTypePlural} with empty fallback`, category: 'control', tags: ['for', 'loop', t.contentTypePlural] },
      { snippet_id: -22, snippet_name: 'Status Badge', snippet_code: '{switch value="{status}"}\n  {case match="published"}<span class="badge-green">Published</span>\n  {case match="draft"}<span class="badge-yellow">Draft</span>\n  {case match="pending"}<span class="badge-blue">Pending</span>\n  {default}<span class="badge-gray">Unknown</span>\n{/switch}', description: 'Status indicator with switch', category: 'control', tags: ['switch', 'status', 'badge'] },
      
      // Layout Components - Dynamic
      { snippet_id: -30, snippet_name: 'Hero Section', snippet_code: '{ikb_section type="hero" bg="gradient"}\n  {ikb_container size="large"}\n    {ikb_text size="3xl" weight="bold"}{site.name}{/ikb_text}\n    {ikb_text size="lg"}{site.tagline}{/ikb_text}\n  {/ikb_container}\n{/ikb_section}', description: 'Full hero section layout', category: 'layout', tags: ['hero', 'section', 'layout'] },
      { snippet_id: -31, snippet_name: `${t.contentType.charAt(0).toUpperCase() + t.contentType.slice(1)} Card`, snippet_code: `{ikb_card}\n  {ikb_image src="{${t.contentType}.thumbnail}" alt="{${t.contentType}.title}" /}\n  {ikb_text size="lg" weight="semibold"}{${t.contentType}.title | esc_html}{/ikb_text}\n  {ikb_text size="sm" color="gray"}{${t.contentType}.${t.excerpt} | truncate:100}{/ikb_text}\n{/ikb_card}`, description: `Reusable ${t.contentType} card component`, category: 'layout', tags: ['card', 'component', t.contentType] },
      { snippet_id: -32, snippet_name: 'Grid Layout', snippet_code: `{ikb_grid cols=3 gap="md"}\n  {for items="{${t.contentTypePlural}}" as="${t.contentType}"}\n    {ikb_card}{${t.contentType}.title}{/ikb_card}\n  {/for}\n{/ikb_grid}`, description: 'Grid with dynamic items', category: 'layout', tags: ['grid', 'layout', 'loop'] },
    ];
    
    // CMS-specific snippets
    const cmsSpecificSnippets: Snippet[] = [];
    
    if (cms === 'wordpress' || cms === 'generic') {
      cmsSpecificSnippets.push(
        { snippet_id: -40, snippet_name: 'WP Post Query', snippet_code: '{wp:query type="post" limit="10" category="news"}\n  {for items="{posts}" as="post"}\n    {ikb_card}\n      {ikb_text size="xl"}{post.title | esc_html}{/ikb_text}\n    {/ikb_card}\n  {/for}\n{/wp:query}', description: 'WordPress namespaced query', category: 'wordpress', tags: ['wordpress', 'query', 'posts'] },
        { snippet_id: -41, snippet_name: 'WP Safe Content', snippet_code: '{post.content | wp_kses_post}', description: 'WordPress safe HTML output', category: 'wordpress', tags: ['wordpress', 'security', 'html'] },
        { snippet_id: -42, snippet_name: 'WP Featured Image', snippet_code: '{post.thumbnail | esc_url}', description: 'WordPress featured image URL', category: 'wordpress', tags: ['wordpress', 'image', 'thumbnail'] },
        { snippet_id: -43, snippet_name: 'WP Excerpt', snippet_code: '{post.excerpt | wp_trim_words:20 | esc_html}', description: 'WordPress trimmed excerpt', category: 'wordpress', tags: ['wordpress', 'excerpt', 'text'] },
      );
    }
    
    if (cms === 'joomla' || cms === 'generic') {
      cmsSpecificSnippets.push(
        { snippet_id: -50, snippet_name: 'Joomla Articles Query', snippet_code: '{joomla:articles category="blog" limit="5"}\n  {for items="{articles}" as="article"}\n    <h3>{article.title | esc_html}</h3>\n    <p>{article.introtext | strip_tags | truncate:150}</p>\n  {/for}\n{/joomla:articles}', description: 'Joomla namespaced articles', category: 'joomla', tags: ['joomla', 'articles'] },
        { snippet_id: -51, snippet_name: 'Joomla Intro Text', snippet_code: '{article.introtext | strip_tags | truncate:200 | esc_html}', description: 'Joomla article intro text', category: 'joomla', tags: ['joomla', 'introtext', 'excerpt'] },
        { snippet_id: -52, snippet_name: 'Joomla Full Article', snippet_code: '{article.fulltext | raw}', description: 'Joomla full article content', category: 'joomla', tags: ['joomla', 'fulltext', 'content'] },
      );
    }
    
    if (cms === 'drupal' || cms === 'generic') {
      cmsSpecificSnippets.push(
        { snippet_id: -60, snippet_name: 'Drupal Nodes Query', snippet_code: '{drupal:nodes type="article" limit="5"}\n  {for items="{nodes}" as="node"}\n    <h3>{node.title | esc_html}</h3>\n    <p>{node.body | strip_tags | truncate:150}</p>\n  {/for}\n{/drupal:nodes}', description: 'Drupal namespaced nodes', category: 'drupal', tags: ['drupal', 'nodes'] },
        { snippet_id: -61, snippet_name: 'Drupal Body Field', snippet_code: '{node.body | strip_tags | truncate:200 | esc_html}', description: 'Drupal node body field', category: 'drupal', tags: ['drupal', 'body', 'content'] },
        { snippet_id: -62, snippet_name: 'Drupal Translation', snippet_code: "{node.title | t}", description: 'Drupal translated text', category: 'drupal', tags: ['drupal', 'translation', 'i18n'] },
        { snippet_id: -63, snippet_name: 'Drupal Taxonomy', snippet_code: '{for items="{node.taxonomy}" as="term"}\n  <span class="tag">{term.name | esc_html}</span>\n{/for}', description: 'Drupal taxonomy terms', category: 'drupal', tags: ['drupal', 'taxonomy', 'tags'] },
      );
    }
    
    return [...baseSnippets, ...cmsSpecificSnippets];
  };

  // Fetch grammar
  const { data: grammar } = useQuery<Grammar>({
    queryKey: ['dsl-grammar'],
    queryFn: async () => {
      const res = await fetch('/api/v1/dsl/grammar');
      if (!res.ok) throw new Error('Failed to fetch grammar');
      return res.json();
    }
  });

  // Fetch snippets
  const { data: snippetsData, refetch: refetchSnippets } = useQuery<{ snippets: Snippet[] }>({
    queryKey: ['dsl-snippets'],
    queryFn: async () => {
      const res = await fetch('/api/v1/dsl/snippets');
      if (!res.ok) throw new Error('Failed to fetch snippets');
      return res.json();
    }
  });

  // Fetch instances for execution context
  const { data: instancesData } = useQuery<{ instances: Instance[] }>({
    queryKey: ['instances'],
    queryFn: async () => {
      const res = await fetch('/api/v1/instances', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch instances');
      return res.json();
    }
  });

  const instances = instancesData?.instances || [];
  const activeInstances = instances.filter(i => i.status === 'active');

  // Get selected instance's CMS type (must be after instances are defined)
  const selectedCmsType = (() => {
    if (demoMode) return 'generic';
    const inst = instances.find(i => i.instance_id === selectedInstance);
    return inst?.cms_type || 'generic';
  })();
  
  const currentTerms = cmsTerminology[selectedCmsType] || cmsTerminology.generic;
  
  // Generate built-in snippets based on current CMS context
  const builtInSnippets = getBuiltInSnippets(currentTerms, selectedCmsType);

  // Compile query (Validate)
  const compileMutation = useMutation({
    mutationFn: async (q: string) => {
      const res = await fetch('/api/v1/dsl/compile', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          query: q, 
          demo: demoMode,
          instance_id: selectedInstance || undefined
        })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(errorData.error || `Request failed with status ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        setLastAst(data.ast || null);
        toast.success(`✓ Valid! Compiled in ${data.compilation_time_ms}ms`);
      } else {
        toast.error(data.error || 'Validation failed');
      }
    },
    onError: (error: Error) => {
      toast.error(`Validation error: ${error.message}`);
    }
  });

  // Execute query (Preview)
  const executeMutation = useMutation({
    mutationFn: async (q: string) => {
      const res = await fetch('/api/v1/dsl/execute', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          query: q, 
          demo: demoMode,
          instance_id: selectedInstance || undefined
        })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(errorData.error || `Request failed with status ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        setPreviewHtml(data.html);
        setLastAst(data.ast || null);
        const inst = selectedInstance ? instances.find(i => i.instance_id === selectedInstance) : null;
        const instanceName = inst?.domain || inst?.instance_name || inst?.name;
        toast.success(`Executed in ${data.execution_time_ms}ms${data.demo_mode ? ' (Demo)' : instanceName ? ` on ${instanceName}` : ''}`);
      } else {
        toast.error(data.error || 'Execution failed');
      }
    },
    onError: (error: Error) => {
      toast.error(`Execution error: ${error.message}`);
    }
  });

  // Save snippet
  const saveSnippetMutation = useMutation({
    mutationFn: async () => {
      const name = prompt('Enter snippet name:');
      if (!name) return null;
      
      const res = await fetch('/api/v1/dsl/snippets', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          snippet_name: name,
          snippet_code: query,
          category: 'custom'
        })
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data?.success) {
        toast.success('Snippet saved');
        refetchSnippets();
      }
    }
  });

  const copyToClipboard = () => {
    navigator.clipboard.writeText(query);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  const insertParameter = (param: string, value?: string) => {
    const insertion = value ? `${param}=${value}` : `${param}=`;
    // Insert before closing brace
    const newQuery = query.replace(/}$/, ` ${insertion}}`);
    setQuery(newQuery);
  };

  const loadSnippet = (snippet: Snippet) => {
    setQuery(snippet.snippet_code);
    setActiveTab('builder');
    toast.success(`Loaded: ${snippet.snippet_name}`);
  };

  const insertFilter = (filter: string) => {
    // Insert filter at cursor or append to expression
    // If query contains an expression like {var}, add filter: {var | filter}
    const filterSyntax = ` | ${filter}`;
    if (query.includes('}')) {
      // Find last closing brace that's part of an expression (not ikb_query)
      const lastBrace = query.lastIndexOf('}');
      const newQuery = query.slice(0, lastBrace) + filterSyntax + query.slice(lastBrace);
      setQuery(newQuery);
    } else {
      setQuery(query + filterSyntax);
    }
    toast.success(`Added filter: ${filter}`);
  };

  const insertControlStructure = (code: string) => {
    setQuery(code);
    toast.success('Control structure template inserted');
  };

  const insertOperator = (op: string) => {
    // Insert operator at the end of current query (before closing brace if present)
    if (query.endsWith('}')) {
      const newQuery = query.slice(0, -1) + ` ${op} ` + '}';
      setQuery(newQuery);
    } else {
      setQuery(query + ` ${op} `);
    }
  };

  const savedSnippets = snippetsData?.snippets || [];
  const allSnippets = [...builtInSnippets, ...savedSnippets];
  
  // Filter snippets by search and category
  const filteredSnippets = allSnippets.filter(s => {
    const matchesSearch = snippetSearch === '' || 
      s.snippet_name.toLowerCase().includes(snippetSearch.toLowerCase()) ||
      s.description?.toLowerCase().includes(snippetSearch.toLowerCase()) ||
      s.tags?.some(t => t.toLowerCase().includes(snippetSearch.toLowerCase()));
    const matchesCategory = snippetCategory === 'all' || s.category === snippetCategory;
    return matchesSearch && matchesCategory;
  });
  
  const snippetCategories = [...new Set(allSnippets.map(s => s.category))];
  
  const groupedSnippets = filteredSnippets.reduce((acc, s) => {
    if (!acc[s.category]) acc[s.category] = [];
    acc[s.category].push(s);
    return acc;
  }, {} as Record<string, Snippet[]>);

  // Filter reference items
  const filterReferenceItems = <T extends { name: string; description?: string }>(items: T[]) => {
    if (referenceSearch === '') return items;
    return items.filter(item => 
      item.name.toLowerCase().includes(referenceSearch.toLowerCase()) ||
      item.description?.toLowerCase().includes(referenceSearch.toLowerCase())
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">DSL Query Builder</h1>
          <p className="text-gray-600 mt-1">Build dynamic content queries using Ikabud DSL</p>
        </div>
        {/* CMS Context Indicator */}
        {!demoMode && selectedInstance && selectedCmsType !== 'generic' && (
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            selectedCmsType === 'wordpress' ? 'bg-blue-50 border border-blue-200' :
            selectedCmsType === 'joomla' ? 'bg-orange-50 border border-orange-200' :
            selectedCmsType === 'drupal' ? 'bg-cyan-50 border border-cyan-200' :
            'bg-gray-50 border border-gray-200'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              selectedCmsType === 'wordpress' ? 'bg-blue-500' :
              selectedCmsType === 'joomla' ? 'bg-orange-500' :
              selectedCmsType === 'drupal' ? 'bg-cyan-500' :
              'bg-gray-500'
            }`} />
            <span className={`text-sm font-medium ${
              selectedCmsType === 'wordpress' ? 'text-blue-700' :
              selectedCmsType === 'joomla' ? 'text-orange-700' :
              selectedCmsType === 'drupal' ? 'text-cyan-700' :
              'text-gray-700'
            }`}>
              {selectedCmsType === 'wordpress' ? 'WordPress' :
               selectedCmsType === 'joomla' ? 'Joomla' :
               selectedCmsType === 'drupal' ? 'Drupal' : selectedCmsType} Mode
            </span>
            <span className="text-xs text-gray-500">
              ({currentTerms.contentTypePlural}, {currentTerms.excerpt})
            </span>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">What is DSL?</h3>
            <p className="text-sm text-blue-700 mt-1">
              DSL (Domain Specific Language) lets you embed dynamic CMS content in templates without writing PHP.
              Use queries like <code className="bg-blue-100 px-1 rounded">{'{ikb_query type=post limit=5}'}</code> to 
              fetch posts, pages, products, and more with customizable layouts.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('builder')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'builder' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Code2 className="w-4 h-4 inline mr-2" />
            Query Builder
          </button>
          <button
            onClick={() => setActiveTab('snippets')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'snippets' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileCode className="w-4 h-4 inline mr-2" />
            Snippets Library
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">{allSnippets.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('reference')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reference' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            DiSyL Reference
          </button>
          <a
            href="https://github.com/aKira041795/Ikabud-CMS-Kernel/blob/master/docs/DISYL_GRAMMAR_v1.2.ebnf"
            target="_blank"
            rel="noopener noreferrer"
            className="py-2 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 hover:text-gray-700 flex items-center"
          >
            <ExternalLink className="w-4 h-4 inline mr-2" />
            Full Grammar Spec
          </a>
        </nav>
      </div>

      {activeTab === 'builder' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Query Editor */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Query Editor</h2>
                <div className="flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="btn-secondary text-sm flex items-center gap-1"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    onClick={() => saveSnippetMutation.mutate()}
                    className="btn-secondary text-sm flex items-center gap-1"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                </div>
              </div>
              <div className="p-4">
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full h-32 font-mono text-sm p-4 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-green-400"
                  placeholder="{ikb_query type=post limit=10}"
                  spellCheck={false}
                />
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => compileMutation.mutate(query)}
                    disabled={compileMutation.isPending || !query.trim()}
                    className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {compileMutation.isPending ? (
                      <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {compileMutation.isPending ? 'Validating...' : 'Validate'}
                  </button>
                  <button
                    onClick={() => executeMutation.mutate(query)}
                    disabled={executeMutation.isPending || !query.trim() || (!demoMode && !selectedInstance)}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!demoMode && !selectedInstance ? 'Select an instance first' : ''}
                  >
                    {executeMutation.isPending ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    {executeMutation.isPending ? 'Executing...' : 'Execute & Preview'}
                  </button>
                </div>
                
                {/* Execution Context */}
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Execute on:</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="execMode"
                      checked={demoMode}
                      onChange={() => {
                        setDemoMode(true);
                        setSelectedInstance('');
                      }}
                      className="text-blue-600"
                    />
                    <span className="text-gray-700">Demo Mode</span>
                    <span className="text-xs text-gray-400">(sample data)</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="execMode"
                      checked={!demoMode}
                      onChange={() => setDemoMode(false)}
                      className="text-blue-600"
                    />
                    <span className="text-gray-700">Live Instance</span>
                  </label>
                  {!demoMode && (
                    <select
                      value={selectedInstance}
                      onChange={(e) => setSelectedInstance(e.target.value)}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[250px]"
                    >
                      <option value="">Select instance...</option>
                      {activeInstances.map(instance => (
                        <option key={instance.instance_id} value={instance.instance_id}>
                          {instance.domain || instance.instance_name || instance.name} • {instance.cms_type.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  )}
                  {!demoMode && !selectedInstance && (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Select an instance to execute
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Preview */}
            {previewHtml && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-gray-500" />
                    <h2 className="font-semibold text-gray-900">Preview</h2>
                  </div>
                  <button
                    onClick={() => setShowAst(!showAst)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showAst ? 'Hide AST' : 'Show AST'}
                  </button>
                </div>
                <div 
                  className="p-4 prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
                {showAst && lastAst && (
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Abstract Syntax Tree (AST)</h3>
                    <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                      {JSON.stringify(lastAst, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Errors */}
            {(compileMutation.data?.error || executeMutation.data?.error) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-800">Error</h4>
                  <p className="text-sm text-red-700 mt-1">
                    {compileMutation.data?.error || executeMutation.data?.error}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Parameters Panel */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowGrammar(!showGrammar)}
                className="w-full p-4 border-b border-gray-200 flex items-center justify-between text-left"
              >
                <h2 className="font-semibold text-gray-900">Parameters</h2>
                {showGrammar ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>
              {showGrammar && grammar && (
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {Object.entries(grammar.parameters).map(([key, param]) => (
                    <div 
                      key={key}
                      className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                      onClick={() => insertParameter(key, param.default?.toString())}
                    >
                      <div className="flex items-center justify-between">
                        <code className="text-sm font-medium text-blue-600">{key}</code>
                        {param.required && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">required</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Type: {param.type}</p>
                      {param.values && (
                        <p className="text-xs text-gray-500">
                          Values: {param.values.join(', ')}
                        </p>
                      )}
                      {param.default !== undefined && (
                        <p className="text-xs text-gray-500">Default: {param.default}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Insert */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Quick Insert</h3>
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">Content Types</p>
                <div className="flex flex-wrap gap-2">
                  {['post', 'page', 'product', 'category'].map(type => (
                    <button
                      key={type}
                      onClick={() => insertParameter('type', type)}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      {type}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 font-medium mt-3">Formats</p>
                <div className="flex flex-wrap gap-2">
                  {['card', 'list', 'grid', 'hero'].map(format => (
                    <button
                      key={format}
                      onClick={() => insertParameter('format', format)}
                      className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      {format}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 font-medium mt-3">Layouts</p>
                <div className="flex flex-wrap gap-2">
                  {['vertical', 'grid-2', 'grid-3', 'grid-4'].map(layout => (
                    <button
                      key={layout}
                      onClick={() => insertParameter('layout', layout)}
                      className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                    >
                      {layout}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 font-medium mt-3">Filters (Security)</p>
                <div className="flex flex-wrap gap-2">
                  {['esc_html', 'esc_url', 'esc_attr', 'strip_tags'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => insertFilter(filter)}
                      className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 font-medium mt-3">Filters (Text)</p>
                <div className="flex flex-wrap gap-2">
                  {['upper', 'lower', 'capitalize', 'truncate', 'trim'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => insertFilter(filter)}
                      className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 font-medium mt-3">Filters (Format)</p>
                <div className="flex flex-wrap gap-2">
                  {['date', 'number_format', 'json', 'default', 'raw'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => insertFilter(filter)}
                      className="px-2 py-1 text-xs bg-cyan-100 text-cyan-700 rounded hover:bg-cyan-200"
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 font-medium mt-3">Control Structures</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'if', code: '{if condition="{expr}"}...{/if}' },
                    { label: 'for', code: '{for items="{items}" as="item"}...{/for}' },
                    { label: 'switch', code: '{switch value="{val}"}{case match="x"}...{/switch}' },
                    { label: 'include', code: '{include file="path/to/file" /}' }
                  ].map(ctrl => (
                    <button
                      key={ctrl.label}
                      onClick={() => insertControlStructure(ctrl.code)}
                      className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                    >
                      {ctrl.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 font-medium mt-3">Operators</p>
                <div className="flex flex-wrap gap-2">
                  {['?.', '|', '==', '!=', '&&', '||'].map(op => (
                    <button
                      key={op}
                      onClick={() => insertOperator(op)}
                      className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-mono"
                    >
                      {op}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Examples - CMS Aware */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Quick Examples</h3>
                {selectedCmsType !== 'generic' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    selectedCmsType === 'wordpress' ? 'bg-blue-100 text-blue-700' :
                    selectedCmsType === 'joomla' ? 'bg-orange-100 text-orange-700' :
                    selectedCmsType === 'drupal' ? 'bg-cyan-100 text-cyan-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedCmsType.charAt(0).toUpperCase() + selectedCmsType.slice(1)} Mode
                  </span>
                )}
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {[
                  { label: `Latest ${currentTerms.contentTypePlural.charAt(0).toUpperCase() + currentTerms.contentTypePlural.slice(1)}`, query: `{ikb_query type=${currentTerms.contentType} limit=5 format=card}` },
                  { label: 'Product Grid', query: '{ikb_query type=product limit=8 format=grid layout=grid-4}' },
                  { label: `Hero ${currentTerms.contentType.charAt(0).toUpperCase() + currentTerms.contentType.slice(1)}`, query: `{ikb_query type=${currentTerms.contentType} limit=1 format=hero}` },
                  { label: 'Category List', query: '{ikb_query type=category format=list}' },
                  { label: 'Filter Chain', query: `{${currentTerms.contentType}.title | strip_tags | truncate:50 | upper | esc_html}` },
                  { label: 'Safe Navigation', query: '{user?.profile?.avatar | default:"placeholder.png"}' },
                  { label: 'Array Access', query: '{items[0].title | esc_html}' },
                  { label: 'If Condition', query: '{if condition="{user.role == \'admin\'}"}\n  Admin Content\n{else}\n  User Content\n{/if}' },
                  { label: `${currentTerms.contentType.charAt(0).toUpperCase() + currentTerms.contentType.slice(1)} Loop`, query: `{for items="{${currentTerms.contentTypePlural}}" as="${currentTerms.contentType}" key="index"}\n  {${currentTerms.contentType}.title | esc_html}\n{empty}\n  No ${currentTerms.contentTypePlural} found.\n{/for}` },
                  { label: 'Number Format', query: '{item.price | number_format:2,dec_point=".",thousands_sep=","}' },
                  { label: 'Date Format', query: `{${currentTerms.contentType}.${currentTerms.date} | date:format="F j, Y"}` },
                ].map((example, i) => (
                  <button
                    key={i}
                    onClick={() => setQuery(example.query)}
                    className="w-full text-left p-2 text-sm bg-gray-50 rounded hover:bg-gray-100"
                  >
                    <span className="font-medium text-gray-700">{example.label}</span>
                    <code className="block text-xs text-gray-500 mt-1 truncate">{example.query}</code>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'snippets' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">Snippets Library</h2>
                <p className="text-sm text-gray-500 mt-1">Reusable DSL queries for common use cases</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search snippets..."
                    value={snippetSearch}
                    onChange={(e) => setSnippetSearch(e.target.value)}
                    className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                  />
                  {snippetSearch && (
                    <button
                      onClick={() => setSnippetSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {/* Category Filter */}
                <select
                  value={snippetCategory}
                  onChange={(e) => setSnippetCategory(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Categories</option>
                  {snippetCategories.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="p-4">
            {Object.keys(groupedSnippets).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileCode className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No snippets found</p>
                <p className="text-sm mt-1">{snippetSearch ? 'Try a different search term' : 'Create queries in the builder and save them here'}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedSnippets).map(([category, categorySnippets]) => (
                  <div key={category}>
                    <div className="flex items-center gap-2 mb-3">
                      <Folder className="w-4 h-4 text-gray-400" />
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                        {category}
                      </h3>
                      <span className="text-xs text-gray-400">({categorySnippets.length})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categorySnippets.map(snippet => (
                        <div
                          key={snippet.snippet_id}
                          className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <h4 className="font-medium text-gray-900">{snippet.snippet_name}</h4>
                            {snippet.snippet_id < 0 && (
                              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Built-in</span>
                            )}
                          </div>
                          {snippet.description && (
                            <p className="text-sm text-gray-500 mt-1">{snippet.description}</p>
                          )}
                          {snippet.tags && snippet.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {snippet.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                  <Hash className="w-3 h-3" />{tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <pre className="text-xs bg-gray-900 text-green-400 p-2 rounded mt-2 overflow-x-auto max-h-24">
                            <code>{snippet.snippet_code}</code>
                          </pre>
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => loadSnippet(snippet)}
                              className="flex-1 text-xs bg-blue-600 text-white px-2 py-1.5 rounded hover:bg-blue-700 flex items-center justify-center gap-1"
                            >
                              <Play className="w-3 h-3" /> Use
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(snippet.snippet_code);
                                toast.success('Copied to clipboard');
                              }}
                              className="text-xs bg-gray-100 text-gray-700 px-2 py-1.5 rounded hover:bg-gray-200 flex items-center gap-1"
                            >
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                            {snippet.snippet_id > 0 && (
                              <button
                                onClick={() => {
                                  if (confirm('Delete this snippet?')) {
                                    // TODO: Implement delete
                                    toast.info('Delete not implemented yet');
                                  }
                                }}
                                className="text-xs bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* DiSyL Reference Tab */}
      {activeTab === 'reference' && (
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search components, filters, operators..."
                  value={referenceSearch}
                  onChange={(e) => setReferenceSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {referenceSearch && (
                  <button
                    onClick={() => setReferenceSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              <select
                value={referenceCategory}
                onChange={(e) => setReferenceCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                <option value="components">Components</option>
                <option value="filters">Filters</option>
                <option value="control">Control Structures</option>
                <option value="operators">Operators</option>
                <option value="types">Types</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Components */}
            {(referenceCategory === 'all' || referenceCategory === 'components') && filterReferenceItems(disylReference.components).length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <Code2 className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Components</h3>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{filterReferenceItems(disylReference.components).length}</span>
                </div>
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {filterReferenceItems(disylReference.components).map(comp => (
                    <div key={comp.name} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <code className="text-sm font-semibold text-blue-600">{comp.name}</code>
                        <button
                          onClick={() => { setQuery(comp.syntax); setActiveTab('builder'); toast.success('Loaded to editor'); }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Use →
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{comp.description}</p>
                      <pre className="text-xs bg-gray-900 text-green-400 p-2 rounded mt-2 overflow-x-auto"><code>{comp.syntax}</code></pre>
                      {comp.params && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {comp.params.map(p => (
                            <span key={p} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{p}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters - Dynamic from Adapters */}
            {(referenceCategory === 'all' || referenceCategory === 'filters') && filterReferenceItems(dynamicFilters).length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">Filters</h3>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{filterReferenceItems(dynamicFilters).length}</span>
                    {adaptersLoading && <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />}
                  </div>
                  <button 
                    onClick={() => refetchAdapters()}
                    className="text-xs text-gray-500 hover:text-purple-600 flex items-center gap-1"
                    title="Refresh filters from adapters"
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>
                <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
                  {/* Group by category */}
                  {['security', 'text', 'format', 'logic', 'wordpress', 'joomla', 'drupal', 'universal'].map(cat => {
                    const catFilters = filterReferenceItems(dynamicFilters).filter(f => f.category === cat);
                    if (catFilters.length === 0) return null;
                    
                    const catConfig = {
                      security: { label: 'Security', color: 'red', icon: '🔒' },
                      text: { label: 'Text', color: 'green', icon: '📝' },
                      format: { label: 'Format', color: 'cyan', icon: '🔢' },
                      logic: { label: 'Logic', color: 'gray', icon: '⚙️' },
                      wordpress: { label: 'WordPress', color: 'blue', icon: '🔵' },
                      joomla: { label: 'Joomla', color: 'orange', icon: '🟠' },
                      drupal: { label: 'Drupal', color: 'cyan', icon: '🔷' },
                      universal: { label: 'Universal', color: 'purple', icon: '🌐' },
                    }[cat] || { label: cat, color: 'gray', icon: '📦' };
                    
                    return (
                      <div key={cat} className="mb-4">
                        <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1">
                          <span>{catConfig.icon}</span>
                          <span className="text-sm font-medium text-gray-700">{catConfig.label}</span>
                          <span className="text-xs text-gray-400">({catFilters.length})</span>
                        </div>
                        <div className="space-y-2 pl-2 border-l-2 border-gray-100">
                          {catFilters.map(filter => (
                            <div key={filter.name} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <code className="text-sm font-semibold text-purple-600">{filter.name}</code>
                                  {filter.cms && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      CMS_DISPLAY_CONFIG[filter.cms]?.bgColor || 'bg-gray-100'
                                    } ${CMS_DISPLAY_CONFIG[filter.cms]?.textColor || 'text-gray-600'}`}>
                                      {CMS_DISPLAY_CONFIG[filter.cms]?.label || filter.cms}
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => insertFilter(filter.name)}
                                  className="text-xs text-purple-600 hover:text-purple-800"
                                >
                                  Insert →
                                </button>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{filter.description}</p>
                              <pre className="text-xs bg-gray-900 text-green-400 p-2 rounded mt-2 overflow-x-auto"><code>{filter.syntax}</code></pre>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Control Structures */}
            {(referenceCategory === 'all' || referenceCategory === 'control') && filterReferenceItems(disylReference.controlStructures).length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-semibold text-gray-900">Control Structures</h3>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{filterReferenceItems(disylReference.controlStructures).length}</span>
                </div>
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {filterReferenceItems(disylReference.controlStructures).map(ctrl => (
                    <div key={ctrl.name} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <code className="text-sm font-semibold text-indigo-600">{ctrl.name}</code>
                        <button
                          onClick={() => { setQuery(ctrl.example); setActiveTab('builder'); toast.success('Loaded to editor'); }}
                          className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          Use →
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{ctrl.description}</p>
                      <pre className="text-xs bg-gray-900 text-green-400 p-2 rounded mt-2 overflow-x-auto"><code>{ctrl.syntax}</code></pre>
                      {ctrl.example && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">Example:</span>
                          <pre className="text-xs bg-gray-100 text-gray-700 p-2 rounded mt-1 overflow-x-auto"><code>{ctrl.example}</code></pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Operators */}
            {(referenceCategory === 'all' || referenceCategory === 'operators') && filterReferenceItems(disylReference.operators).length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <Hash className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-gray-900">Operators</h3>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{filterReferenceItems(disylReference.operators).length}</span>
                </div>
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {filterReferenceItems(disylReference.operators).map(op => (
                    <div key={op.name} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <code className="text-sm font-semibold text-amber-600 font-mono">{op.name}</code>
                        <button
                          onClick={() => insertOperator(op.name)}
                          className="text-xs text-amber-600 hover:text-amber-800"
                        >
                          Insert →
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{op.description}</p>
                      <pre className="text-xs bg-gray-900 text-green-400 p-2 rounded mt-2 overflow-x-auto"><code>{op.syntax}</code></pre>
                      {op.example && (
                        <pre className="text-xs bg-gray-100 text-gray-700 p-2 rounded mt-1 overflow-x-auto"><code>{op.example}</code></pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Types */}
            {(referenceCategory === 'all' || referenceCategory === 'types') && filterReferenceItems(disylReference.types).length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-teal-600" />
                  <h3 className="font-semibold text-gray-900">Types</h3>
                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">{filterReferenceItems(disylReference.types).length}</span>
                </div>
                <div className="p-4 grid grid-cols-2 gap-2">
                  {filterReferenceItems(disylReference.types).map(type => (
                    <div key={type.name} className="border border-gray-100 rounded p-2 hover:bg-gray-50">
                      <code className="text-sm font-semibold text-teal-600">{type.name}</code>
                      <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
